import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useResource } from "../../hooks/useResource.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";
import { ImageUpload } from "../../components/upload/ImageUpload.jsx";

const DEFAULTS = {
  name: "",
  role: "",
  photo: "",
  bio: "",
  socialLinks: { linkedin: "", instagram: "", facebook: "" },
  sortOrder: 0,
};

function buildPayload(values) {
  const out = { ...values };
  if (out.sortOrder !== "") out.sortOrder = Number(out.sortOrder);
  return out;
}

export default function TeamFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new" || !id;

  const { detail, create, update } = useResource("team", { toastLabel: "team member" });
  const { data: existing, isLoading } = detail(isNew ? null : id);

  const form = useForm({ defaultValues: DEFAULTS });
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (existing) {
      reset({
        ...DEFAULTS,
        ...existing,
        socialLinks: {
          ...DEFAULTS.socialLinks,
          ...(existing.socialLinks ?? {}),
        },
      });
    }
  }, [existing, reset]);

  useEffect(() => {
    document.title = isNew ? "New Team Member · Admin" : "Edit Team Member · Admin";
  }, [isNew]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = buildPayload(values);
    if (isNew) {
      await new Promise((resolve, reject) => {
        create.mutate(payload, {
          onSuccess: (created) => {
            navigate(`/team/${created._id}`);
            resolve();
          },
          onError: reject,
        });
      });
    } else {
      await new Promise((resolve, reject) => {
        update.mutate({ id, data: payload }, { onSuccess: resolve, onError: reject });
      });
    }
  });

  if (!isNew && isLoading) return <PageLoader />;

  return (
    <FormShell
      title={isNew ? "New Team Member" : `Edit "${existing?.name ?? ""}"`}
      backTo="/team"
      backLabel="Back to team"
      onSubmit={onSubmit}
      primaryAction={
        <Button type="submit" loading={isSubmitting}>
          {isNew ? "Create member" : "Save changes"}
        </Button>
      }
      secondaryAction={
        <Button type="button" variant="secondary" onClick={() => navigate("/team")}>
          Cancel
        </Button>
      }
      sidebar={
        <>
          <FormSection title="Photo">
            <Controller
              control={control}
              name="photo"
              render={({ field }) => (
                <ImageUpload
                  value={field.value}
                  onChange={field.onChange}
                  subdir="team"
                  aspect="square"
                  hint="Square headshot, ≥600 px. Falls back to a stock portrait when blank."
                />
              )}
            />
          </FormSection>

          <FormSection title="Sort">
            <Input type="number" label="Sort order" {...register("sortOrder")} />
          </FormSection>
        </>
      }
    >
      <FormSection title="Basics">
        <Input
          label="Full name"
          required
          error={errors.name?.message}
          {...register("name", { required: "Name is required" })}
        />
        <Input
          label="Role"
          hint="e.g. Founder, Head of Design, Construction Manager"
          {...register("role")}
        />
        <Textarea
          label="Bio"
          rows={5}
          hint="One short paragraph shown on the public Our Team card."
          {...register("bio")}
        />
      </FormSection>

      <FormSection title="Social links" description="Optional — only the icons that have a URL render publicly.">
        <Input
          label="LinkedIn URL"
          type="url"
          placeholder="https://linkedin.com/in/…"
          {...register("socialLinks.linkedin")}
        />
        <Input
          label="Instagram URL"
          type="url"
          placeholder="https://instagram.com/…"
          {...register("socialLinks.instagram")}
        />
        <Input
          label="Facebook URL"
          type="url"
          placeholder="https://facebook.com/…"
          {...register("socialLinks.facebook")}
        />
      </FormSection>
    </FormShell>
  );
}
